/**
 * A domain is drawn as a dashed ring enclosing every one of its nodes. When two
 * rings intersect, the nodes in the lens between them look like they belong to
 * both domains. These helpers measure the rings and push intersecting ones
 * apart, so a node can never sit inside a ring it does not belong to.
 */

/** Space between the outermost node of a domain and its ring. */
export const DOMAIN_RING_PAD = 12;

/** Clear space kept between two rings once they have been separated. */
export const DOMAIN_RING_GAP = 24;

/**
 * Fraction of the remaining overlap corrected per tick. Applied to velocity, so
 * the effective step is larger than this; low enough to settle without ringing.
 */
const SEPARATION_STRENGTH = 0.2;

export interface ClusterPoint {
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
}

export interface DomainCircle<T extends ClusterPoint> {
  domain: string;
  cx: number;
  cy: number;
  /** Includes DOMAIN_RING_PAD, so this is the radius actually drawn. */
  r: number;
  members: T[];
}

interface Circle {
  cx: number;
  cy: number;
  r: number;
}

/** Floating-point slack when testing containment, in px. */
const EPSILON = 1e-7;

function contains(c: Circle, x: number, y: number): boolean {
  return Math.hypot(x - c.cx, y - c.cy) <= c.r + EPSILON;
}

function circleFromTwo(ax: number, ay: number, bx: number, by: number): Circle {
  const cx = (ax + bx) / 2;
  const cy = (ay + by) / 2;
  return { cx, cy, r: Math.hypot(ax - cx, ay - cy) };
}

/** Circumcircle of three points, or null when they are collinear. */
function circleFromThree(
  ax: number, ay: number,
  bx: number, by: number,
  cx: number, cy: number,
): Circle | null {
  const d = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));
  if (Math.abs(d) < EPSILON) return null;

  const a2 = ax * ax + ay * ay;
  const b2 = bx * bx + by * by;
  const c2 = cx * cx + cy * cy;
  const ux = (a2 * (by - cy) + b2 * (cy - ay) + c2 * (ay - by)) / d;
  const uy = (a2 * (cx - bx) + b2 * (ax - cx) + c2 * (bx - ax)) / d;

  return { cx: ux, cy: uy, r: Math.hypot(ax - ux, ay - uy) };
}

/**
 * Welzl's minimum enclosing circle, incremental form. Linear in expectation
 * provided the input order is random, which `groupByDomain` guarantees.
 */
function minEnclosingCircle<T extends ClusterPoint>(points: T[]): Circle {
  let c: Circle = { cx: points[0].x ?? 0, cy: points[0].y ?? 0, r: 0 };

  for (let i = 1; i < points.length; i++) {
    const pix = points[i].x ?? 0;
    const piy = points[i].y ?? 0;
    if (contains(c, pix, piy)) continue;

    c = { cx: pix, cy: piy, r: 0 };
    for (let j = 0; j < i; j++) {
      const pjx = points[j].x ?? 0;
      const pjy = points[j].y ?? 0;
      if (contains(c, pjx, pjy)) continue;

      c = circleFromTwo(pix, piy, pjx, pjy);
      for (let k = 0; k < j; k++) {
        const pkx = points[k].x ?? 0;
        const pky = points[k].y ?? 0;
        if (contains(c, pkx, pky)) continue;

        c = circleFromThree(pix, piy, pjx, pjy, pkx, pky) ?? c;
      }
    }
  }

  return c;
}

/**
 * Smallest circle covering every member, plus the largest member's own radius
 * and the ring padding. Anchoring on the minimum enclosing circle rather than
 * the centroid matters: a domain whose nodes are mostly tight but which has a
 * few strays pulled away by cross-domain edges would otherwise get a radius set
 * by the full centroid-to-stray distance, roughly double what is needed.
 */
export function measureDomainCircles<T extends ClusterPoint>(
  membersByDomain: Map<string, T[]>,
  radiusOf: (member: T) => number,
): DomainCircle<T>[] {
  const circles: DomainCircle<T>[] = [];

  membersByDomain.forEach((members, domain) => {
    if (members.length === 0) return;

    const { cx, cy, r } = minEnclosingCircle(members);

    let maxMemberRadius = 0;
    for (const m of members) {
      const mr = radiusOf(m);
      if (mr > maxMemberRadius) maxMemberRadius = mr;
    }

    circles.push({
      domain,
      cx,
      cy,
      r: r + maxMemberRadius + DOMAIN_RING_PAD,
      members,
    });
  });

  return circles;
}

/**
 * Nudges the members of intersecting domains apart along the axis joining their
 * centres. The correction is split by member count so a small domain is not
 * dragged across the canvas by a large one.
 */
export function separateDomainCircles<T extends ClusterPoint>(
  circles: DomainCircle<T>[],
): void {
  for (let i = 0; i < circles.length; i++) {
    for (let j = i + 1; j < circles.length; j++) {
      const a = circles[i];
      const b = circles[j];

      let dx = b.cx - a.cx;
      let dy = b.cy - a.cy;
      let dist = Math.hypot(dx, dy);
      if (dist === 0) {
        // Coincident centres have no axis to push along; pick a stable one.
        dx = 1;
        dy = 0;
        dist = 1;
      }

      const overlap = a.r + b.r + DOMAIN_RING_GAP - dist;
      if (overlap <= 0) continue;

      const ux = dx / dist;
      const uy = dy / dist;
      const total = a.members.length + b.members.length;
      const shiftA = overlap * (b.members.length / total) * SEPARATION_STRENGTH;
      const shiftB = overlap * (a.members.length / total) * SEPARATION_STRENGTH;

      for (const m of a.members) {
        m.vx = (m.vx ?? 0) - ux * shiftA;
        m.vy = (m.vy ?? 0) - uy * shiftA;
      }
      for (const m of b.members) {
        m.vx = (m.vx ?? 0) + ux * shiftB;
        m.vy = (m.vy ?? 0) + uy * shiftB;
      }
    }
  }
}

/**
 * Groups nodes by domain. Members are shuffled because `minEnclosingCircle`
 * only hits its expected linear time on randomly ordered input.
 *
 * `keyOf` selects the grouping facet, letting callers cluster by region, env or
 * CSP with the same geometry the domain rings use.
 */
export function groupByDomain<T extends ClusterPoint>(
  members: T[],
  keyOf: (m: T) => string = (m: any) => m.domain,
): Map<string, T[]> {
  const byDomain = new Map<string, T[]>();
  for (const m of members) {
    const key = keyOf(m);
    const existing = byDomain.get(key);
    if (existing) existing.push(m);
    else byDomain.set(key, [m]);
  }

  byDomain.forEach(group => {
    for (let i = group.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [group[i], group[j]] = [group[j], group[i]];
    }
  });

  return byDomain;
}
